import { BindingError } from "../BindingError.js";
import { ErrorAccessor } from "./ErrorAccessor.js";
import { TreeNodeUtils } from "../utils/TreeNodeUtils.js";
import { PlaceholderParametersGenerator } from "../markers/PlaceholderParametersGenerator.js";
import { QueryLanguage } from "../queryLanguage/QueryLanguage.js";
import { PlaceholderGenerator } from "../markers/PlaceholderGenerator.js";
import { getEntityMarker } from "../core/state/getEntityMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
class EntityAccessor {
  constructor(state, operations, runtimeId, key, name, fieldData, dataFromServer, hasUnpersistedChanges, errors, environment, getAccessor) {
    this.state = state;
    this.operations = operations;
    this.runtimeId = runtimeId;
    this.key = key;
    this.name = name;
    this.fieldData = fieldData;
    this.dataFromServer = dataFromServer;
    this.hasUnpersistedChanges = hasUnpersistedChanges;
    this.errors = errors;
    this.environment = environment;
    this.getAccessor = getAccessor;
  }
  get idOnServer() {
    return this.runtimeId.existsOnServer ? this.runtimeId.value : void 0;
  }
  /**
   * Note that for entities that don't yet exist on server this will return a dummy id.
   */
  get id() {
    return this.runtimeId.value;
  }
  get existsOnServer() {
    return this.runtimeId.existsOnServer;
  }
  //
  addError(error) {
    return this.operations.addError(this.state, ErrorAccessor.normalizeError(error));
  }
  addEventListener(event, listener) {
    return this.operations.addEventListener(this.state, event, listener);
  }
  batchUpdates(performUpdates) {
    this.operations.batchUpdates(this.state, performUpdates);
  }
  connectEntityAtField(field, entityToConnect) {
    this.operations.connectEntityAtField(this.state, field, entityToConnect);
  }
  disconnectEntityAtField(field, initializeReplacement) {
    this.operations.disconnectEntityAtField(this.state, field, initializeReplacement);
  }
  deleteEntity() {
    this.operations.deleteEntity(this.state);
  }
  //
  updateValues(fieldValuePairs) {
    this.batchUpdates((getAccessor) => {
      const entries = Array.isArray(fieldValuePairs) ? fieldValuePairs : Object.entries(fieldValuePairs);
      for (const [field, value] of entries) {
        getAccessor().getField(field).updateValue(value);
      }
    });
  }
  /**
   * Please keep in mind that this method signature is literally impossible to implement safely. The generic parameter
   * is really just a way to succinctly write a type cast. Nothing more, really.
   */
  getField(field) {
    return this.getRelativeSingleField(QueryLanguage.desugarRelativeSingleField(field, this.environment));
  }
  getEntity(entity) {
    return this.getRelativeSingleEntity(QueryLanguage.desugarRelativeSingleEntity(entity, this.environment));
  }
  getEntityList(entityList) {
    return this.getRelativeEntityList(QueryLanguage.desugarRelativeEntityList(entityList, this.environment));
  }
  //
  /**
   * @see EntityAccessor.getField
   */
  getRelativeSingleField(field) {
    const entity = this.getRelativeSingleEntity(field);
    const fieldPlaceholder = PlaceholderGenerator.getFieldPlaceholder(field.field);
    const accessor = entity.fieldData.get(fieldPlaceholder);
    if (accessor) {
      return accessor.getAccessor();
    }
    TreeNodeUtils.resolveColumn(entity.environment, field.field);
    throw new BindingError(
      `EntityAccessor: cannot access field '${field.field}' on ${TreeNodeUtils.describeLocation(entity.environment)}.
The cause of the error is that this relation has not been registered during the static rendering process. As a result, it lacks a required marker and accessor.`
    );
  }
  getRelativeSingleEntity(relativeSingleEntity) {
    let relativeTo = this;
    for (const hasOneRelation of relativeSingleEntity.hasOneRelationPath) {
      const fieldPlaceholder = PlaceholderGenerator.getHasOneRelationPlaceholder(hasOneRelation);
      const accessor = relativeTo.fieldData.get(fieldPlaceholder);
      if (!accessor) {
        return this.raiseInvalidHasOneRelationError(relativeTo, hasOneRelation);
      }
      relativeTo = accessor.getAccessor();
    }
    return relativeTo;
  }
  getRelativeEntityList(entityList) {
    const entity = this.getRelativeSingleEntity(entityList);
    const fieldPlaceholder = PlaceholderGenerator.getHasManyRelationPlaceholder(entityList.hasManyRelation);
    const accessor = entity.fieldData.get(fieldPlaceholder);
    if (!accessor) {
      return this.raiseInvalidHasManyRelationError(entity, entityList.hasManyRelation);
    }
    return accessor.getAccessor();
  }
  raiseInvalidHasOneRelationError(relativeTo, hasOneRelation) {
    TreeNodeUtils.resolveHasOneRelation(relativeTo.environment, hasOneRelation.field, !!hasOneRelation.reducedBy);
    const entityMarker = relativeTo.getMarker();
    const possibleMarkers = this.getMarkersOfType(entityMarker.fields.markers, HasOneRelationMarker, hasOneRelation.field);
    const markerParams = possibleMarkers.map((it) => PlaceholderParametersGenerator.createHasOneRelationParameters(it.parameters));
    const relationParams = PlaceholderParametersGenerator.createHasOneRelationParameters(hasOneRelation);
    this.raiseUndefinedRelationError(hasOneRelation.field, relativeTo.environment, relationParams, markerParams);
  }
  raiseInvalidHasManyRelationError(entity, hasManyRelation) {
    TreeNodeUtils.resolveHasManyRelation(entity.environment, hasManyRelation.field);
    const entityMarker = entity.getMarker();
    const possibleMarkers = this.getMarkersOfType(entityMarker.fields.markers, HasManyRelationMarker, hasManyRelation.field);
    const markerParams = possibleMarkers.map((it) => PlaceholderParametersGenerator.createHasManyRelationParameters(it.parameters));
    const relationParams = PlaceholderParametersGenerator.createHasManyRelationParameters(hasManyRelation);
    this.raiseUndefinedRelationError(hasManyRelation.field, entity.environment, relationParams, markerParams);
  }
  raiseUndefinedRelationError(field, environment, relationParams, otherMarkersParams) {
    const hint = this.createHintMessage(otherMarkersParams);
    throw new BindingError(
      `EntityAccessor: cannot access relation '${field}' on ${TreeNodeUtils.describeLocation(environment)}.
The cause of the error is that this relation has not been registered during the static rendering process. As a result, it lacks a required marker and accessor.

Provided parameters:
` + JSON.stringify(relationParams) + hint
    );
  }
  createHintMessage(params) {
    if (params.length === 0) {
      return "";
    }
    const serializedParams = params.map((it) => JSON.stringify(it)).join("\n");
    const message = params.length === 1 ? "However, there is existing marker for the same field, but it have different parameters:" : "However, there are existing markers for the same field, but they have different parameters:";
    return `

${message}
${serializedParams}`;
  }
  getMarkersOfType(markers, markerConstructor, fieldName) {
    return Array.from(markers.values()).filter((it) => it instanceof markerConstructor && it.parameters.field === fieldName);
  }
  getParent() {
    const blueprint = this.state.blueprint;
    if (blueprint.type === "subTree") {
      return void 0;
    }
    return blueprint.parent.getAccessor();
  }
  getMarker() {
    return getEntityMarker(this.state);
  }
}
export {
  EntityAccessor
};
//# sourceMappingURL=EntityAccessor.js.map
